/**
 * The one place the app reads customer configuration.
 *
 * The customer is chosen at build time by REACT_APP_CUSTOMER (default
 * "windward"). Nothing else in src/ may name a customer: that is what keeps
 * this a product repo rather than a fork per customer (DESIGN.md section 2).
 *
 * The two imports below are generated into src/ by scripts/generate-config.mjs
 * from config/customers/<id>.json, which npm run config runs before every
 * start, test and build. They are generated rather than imported directly
 * because CRA refuses relative imports that leave src/, and the canonical file
 * has to stay at the repo root for Phase 2's CDK stacks to read.
 */

import config from "./customer.generated";
import logo from "./logo.generated.png";
import organizations from "./organizations.generated";

/** Everything the UI needs to render as this customer. */
export const brand = {
  id: config.id,
  name: config.shellName,
  shortName: config.shortName ?? config.shellName,
  /**
   * The operating companies that share this portal, each billing its own
   * customers (docs/changes/organization-scoping/SPEC.md). Every customer and
   * job belongs to one, by `id`. Each entry carries `id`, `name`, `shortName`,
   * `logo` (the imported asset), `logoAlt` and `remitTo` (`{ name, lines }`,
   * the storage invoice's remit-to block - the name alone when none is
   * configured); one entry is the app as it was, two or more get the
   * organization switch.
   */
  organizations,
  logo,
  logoAlt: config.logo?.alt ?? config.shellName,
  palette: config.palette ?? {},
  support: config.support ?? {},
  auth: config.auth ?? {},
  features: config.features ?? {},
  /* Read by the storage-billing handler, which is why the shape matches the
     three SQL exclusions rather than anything the UI needs. */
  storageBilling: config.storageBilling ?? {
    excludedControlNumbers: [],
    excludedControlNumberPatterns: [],
    excludedCustomerIds: [],
  },
};

/**
 * The Cognito provider name for "Sign in with Microsoft", or null.
 *
 * Needs the config's `auth.microsoft` block and a hosted-UI domain in the build.
 * Without the domain, generate-config.mjs leaves the oauth block out of
 * aws-exports and a federated sign-in would throw inside Amplify - which is the
 * state of the stub build, local development and e2e:boot, so those keep the
 * email and password form.
 */
export const microsoftSignIn =
  brand.auth.microsoft?.providerName &&
  (process.env.REACT_APP_COGNITO_DOMAIN || brand.auth.hostedUIDomain)
    ? brand.auth.microsoft.providerName
    : null;

/** "A, B and C" - the names as a sentence. */
export function sentenceOf(names) {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The organizations' names as a sentence, for the login page. */
export function companiesSentence() {
  return sentenceOf(
    brand.organizations.map((organization) => organization.name),
  );
}

/**
 * True when `email` is allowed to be invited.
 *
 * employee-portal hard-coded `endsWith("@tdmh.com")`. An empty list here means
 * no domain restriction, which is the right default for a customer whose staff
 * may not share one domain - it is not a silent "allow nothing".
 */
export function isInvitableEmail(email) {
  const domains = brand.auth.inviteEmailDomains ?? [];
  if (domains.length === 0) return true;
  const address = String(email || "").toLowerCase();
  return domains.some((d) => address.endsWith(String(d).toLowerCase()));
}

/** "Email must be a @example.com address." - empty when anything is allowed. */
export function inviteDomainHint() {
  const domains = brand.auth.inviteEmailDomains ?? [];
  if (domains.length === 0) return "";
  if (domains.length === 1) return `Email must be a ${domains[0]} address.`;
  return `Email must end in one of: ${domains.join(", ")}.`;
}

export default brand;
